import * as React from "react";
import { useState } from "react";
import { Info, RefreshCw, Check, X, CreditCard, ExternalLink } from "lucide-react";
import { FinancialStatus, updateOverageLimit } from "../services/walletService";
import { getInsufficientFundsMessage } from "../hooks/useFinancialStatus";
import { configService } from "../../../shared/services/configService";

interface InsufficientBalanceWarningProps {
  financialStatus: FinancialStatus;
  userId: string;
  isLoading?: boolean;
  onRechargeClick?: () => void;
  onRefresh?: () => void;
  className?: string;
}

/**
 * Banner de advertencia amigable (azul) con opción de ajuste de límite in-app
 */
export const InsufficientBalanceWarning: React.FC<InsufficientBalanceWarningProps> = ({
  financialStatus,
  userId,
  isLoading = false,
  onRechargeClick,
  onRefresh,
  className = ""
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newLimit, setNewLimit] = useState<string>(financialStatus.overageLimit.toString());
  const [isUpdating, setIsUpdating] = useState(false);

  // No mostrar el banner mientras está cargando o si el usuario tiene saldo suficiente
  if (isLoading || financialStatus.canSendMessage) return null;

  const { title } = getInsufficientFundsMessage(financialStatus.reason);
  
  const handleUpgradePlan = () => {
    if (onRechargeClick) {
      onRechargeClick();
    } else {
      const isB2C = configService.isB2C();
      const rechargeUrl = isB2C 
        ? 'https://app.useworkops.com/settings/usage' 
        : 'https://app.workops.eu/settings/usage';
      window.open(rechargeUrl, '_blank');
    }
  };

  const handleSaveLimit = async () => {
    const numericLimit = parseFloat(newLimit);
    
    if (isNaN(numericLimit) || numericLimit <= financialStatus.overageLimit) {
      return;
    }

    setIsUpdating(true);
    try {
      await updateOverageLimit(userId, numericLimit);
      setIsEditing(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error updating limit:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div 
      className={`
        ml-auto mr-4 mb-1.5 px-3 py-2
        bg-blue-50 border border-blue-100 
        rounded-lg shadow-sm
        flex flex-col gap-2
        w-[92%] max-w-[280px]
        ${className}
      `}
    >
      {/* Header: Info y Upgrade */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Info className="text-blue-600 flex-shrink-0" size={13} />
          <div className="flex flex-col leading-tight min-w-0">
            <span className="text-[10px] font-bold text-blue-900 truncate">
              {title}
            </span>
            <span className="text-[9px] text-blue-700 truncate">
              Limit: ${financialStatus.overageLimit}
            </span>
          </div>
        </div>
        
        <button 
          onClick={handleUpgradePlan}
          className="
            flex items-center gap-0.5
            text-[9px] font-bold text-blue-700 
            hover:text-blue-900 hover:underline
            transition-colors flex-shrink-0
          "
        >
          Upgrade
          <ExternalLink size={9} />
        </button>
      </div>

      {/* Quick Action: Increase Limit */}
      {!isEditing ? (
        <div className="flex items-center gap-1.5">
          <button 
            onClick={() => setIsEditing(true)}
            className="
              flex-1 flex items-center justify-center gap-1
              py-1 text-[10px] font-semibold
              bg-white border border-blue-200 text-blue-700 
              rounded-md hover:bg-blue-100 hover:border-blue-300
              transition-all shadow-sm
            "
          >
            <CreditCard size={10} />
            Increase Limit
          </button>
          
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
              title="Refresh balance"
            >
              <RefreshCw className={`w-3 h-3 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="relative flex-1">
            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-blue-400 text-[10px]">$</span>
            <input 
              type="number" 
              autoFocus
              min={financialStatus.overageLimit + 1}
              step="10"
              className="
                w-full pl-4 pr-1.5 py-1 
                text-[10px] font-medium text-blue-900
                bg-white border border-blue-300 rounded-md 
                focus:ring-1 focus:ring-blue-400 focus:border-blue-400 
                outline-none transition-all
              "
              placeholder="New limit"
              value={newLimit}
              onChange={(e) => setNewLimit(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveLimit();
                if (e.key === 'Escape') setIsEditing(false);
              }}
            />
          </div>
          <button 
            onClick={handleSaveLimit}
            disabled={isUpdating || parseFloat(newLimit) <= financialStatus.overageLimit}
            className="
              p-1 bg-blue-600 text-white rounded-md 
              hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors shadow-sm
            "
            title="Save"
          >
            {isUpdating ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} />}
          </button>
          <button 
            onClick={() => setIsEditing(false)}
            disabled={isUpdating}
            className="
              p-1 bg-white border border-gray-200 text-gray-500 rounded-md 
              hover:bg-gray-50 transition-colors
            "
            title="Cancel"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default InsufficientBalanceWarning;
